import { useLazyListAdminStoresOverallQuery } from 'api/client-domain/client-domain';
import {
  useGetJobsStatusQuery,
  useLazyGetAvailableOffersQuery,
} from 'api/reports-service/reports-service';
import { getDateRangeOptions } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import { Option } from 'components/forms/fields/display-information-field/display-language-tabs/display-language-handle-dropdown/display-language-handle-dropdown.hooks';
import { getPreviousPeriod } from 'components/search/search-filters/date-filter/date-filter.utils';
import {
  luxonDateSearchParam,
  numberSearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import apiDefinitions from './loyalty-performance-api-definitions/loyalty-performance-api-defintitions';

type SearchParamTypes =
  | 'date'
  | 'comparisondate'
  | 'storeIds'
  | 'granularity'
  | 'datatype'
  | 'offerdatatype'
  | 'offertype'
  // | 'campaignId'
  // | 'offerStatus'
  | 'offerIds';

type SearchParamsConfig = {
  [key in SearchParamTypes]: boolean;
};

const useDefaultStartAndEndDate = () => {
  const { data: jobsStatusData } = useGetJobsStatusQuery();
  const loyaltyPulseMaxDate = jobsStatusData?.data.find(
    j => j.jobName === 'LOYALTY_PULSE',
  )?.dataAvailableDate;

  const anchor = loyaltyPulseMaxDate
    ? DateTime.fromISO(loyaltyPulseMaxDate)
    : undefined;

  return {
    defaultStartDate: anchor
      ? anchor.minus({ month: 1 }).startOf('month')
      : DateTime.local().minus({ month: 1 }).startOf('month'),
    defaultEndDate: anchor
      ? anchor.minus({ month: 1 }).endOf('month')
      : DateTime.local().minus({ month: 1 }).endOf('month'),
    maxDate: anchor ?? DateTime.local().minus({ days: 2 }),
  };
};

export const useDateParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const { defaultStartDate, defaultEndDate, maxDate } =
    useDefaultStartAndEndDate();
  return [
    luxonDateSearchParam('startdate', {
      defaultValue: defaultStartDate,
      filter: isFilterVisible
        ? {
            type: 'DateRangeFilter',
            label: t('forms.fields.dateRange.label'),
            showName: true,
            startParamName: 'startdate',
            endParamName: 'enddate',
            predefinedDateOptions: getDateRangeOptions([
              'lpLast7Days',
              'lpLast30Days',
              'lpLast60Days',
              'lpLast90Days',
              'lpLast365Days',
              'last-month',
              'last-quarter',
              'last-year',
              'custom',
            ]),
            clearable: false,
            maxDate,
            anchorDate: maxDate,
          }
        : undefined,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('enddate', {
      defaultValue: defaultEndDate,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
  ];
};

export const useComparisonDateParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const { defaultStartDate, defaultEndDate } = useDefaultStartAndEndDate();
  const [comparisonStartDate, comparisonEndDate] = getPreviousPeriod(
    defaultStartDate,
    defaultEndDate,
  );
  return [
    luxonDateSearchParam('comparisonstartdate', {
      defaultValue: comparisonStartDate,
      filter: isFilterVisible
        ? {
            type: 'ComparisonDateRangeFilter',
            label: t('forms.fields.compareTo.label'),
            showName: true,
            startParamName: 'comparisonstartdate',
            endParamName: 'comparisonenddate',
            compareStartParamName: 'startdate',
            compareEndParamName: 'enddate',
            clearable: false,
          }
        : undefined,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('comparisonenddate', {
      defaultValue: comparisonEndDate,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
  ];
};

export const useAdminStoreIdSearchParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerStores] = useLazyListAdminStoresOverallQuery();

  return [
    stringListSearchParam('storeIds', {
      defaultValue: ['ALL'],
      'data-testid': 'StoresFilter',
      options: [
        {
          label: t(
            'page.transactions.filters.paymentAccountFilter.selectAllOption',
          ),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerStores();
        const dataArray = response?.data?.stores.map(store => ({
          label: store.title,
          id: store.storeId,
        }));

        return [
          {
            label: t('page.transactions.filters.statusFilter.all.label'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.salesSummary.filters.locations'),
          }
        : undefined,
    }),
  ];
};

export const useGranularitySearchParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const granularityOptions = [
    {
      label: t('forms.fields.granularity.options.day'),
      id: 'DAY',
    },
    {
      label: t('forms.fields.granularity.options.week'),
      id: 'WEEK',
    },
    {
      label: t('forms.fields.granularity.options.month'),
      id: 'MONTH',
    },
  ];

  return [
    stringSearchParam('granularity', {
      defaultValue: 'DAY',
      options: granularityOptions,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('forms.fields.granularity.label'),
          }
        : undefined,
    }),
  ];
};

export const useDataTypeSearchParams = (
  isFilterVisible: boolean,
  isOffer: boolean,
) => {
  const { t } = useTranslation();
  const dataOptions: Option[] = [
    {
      label: apiDefinitions.activeMembers.getTitle(t),
      id: 'activeMembersEarnOrBurn',
    },
    !isOffer
      ? {
          label: apiDefinitions.activeMemberPercent.getTitle(t),
          id: 'activeMembersPercent',
        }
      : undefined,
    !isOffer
      ? {
          label: apiDefinitions.allOrdersNetSales.getTitle(t),
          id: 'allOrdersNetSales',
        }
      : undefined,
    isOffer
      ? {
          label: apiDefinitions.averageDaysToRedeem.getTitle(t),
          id: 'averageDaysToRedeem',
        }
      : undefined,
    isOffer
      ? {
          label: t(
            'page.loyaltyPerformanceReport.averageDiscountPercent.label',
          ),
          id: 'averageDiscount',
        }
      : undefined,
    {
      label: apiDefinitions.bonusEarnEvents.getTitle(t),
      id: 'bonusEarnEvents',
    },
    !isOffer
      ? {
          label: apiDefinitions.deletedAccounts.getTitle(t),
          id: 'deletedAccounts',
        }
      : undefined,
    {
      label: apiDefinitions.discountAmount.getTitle(t),
      id: 'discountAmount',
    },
    isOffer
      ? {
          label: apiDefinitions.distributionsSelectedTimePeriod.getTitle(t),
          id: 'distributionsSelectedTimePeriod',
        }
      : undefined,
    {
      label: isOffer
        ? apiDefinitions.inStoreOrders.getTitle(t)
        : apiDefinitions.inStoreLoyaltyOrders.getTitle(t),
      id: 'inStoreLoyaltyOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.inStoreOrdersPercent.getTitle(t)
        : apiDefinitions.inStoreLoyaltyOrdersPercent.getTitle(t),
      id: 'inStoreLoyaltyOrdersPercent',
    },
    {
      label: isOffer
        ? apiDefinitions.averageCheck.getTitle(t)
        : apiDefinitions.loyaltyAverageCheck.getTitle(t),
      id: 'loyaltyAverageCheck',
    },
    {
      label: apiDefinitions.loyaltyBurnOrders.getTitle(t),
      id: 'loyaltyBurnOrdersAllChannels',
    },
    {
      label: apiDefinitions.loyaltyContributionPercentOfSales.getTitle(t),
      id: 'loyaltyContributionOfSalesPercent',
    },
    {
      label: apiDefinitions.loyaltyContributionPercentOfOrders.getTitle(t),
      id: 'loyaltyContributionPercentOfOrders',
    },
    {
      label: apiDefinitions.loyaltyEarnOrders.getTitle(t),
      id: 'loyaltyEarnOrdersAllChannels',
    },
    {
      label: apiDefinitions.loyaltyNetZeroOrders.getTitle(t),
      id: 'loyaltyNetZeroOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.ordersNetSales.getTitle(t)
        : apiDefinitions.loyaltyOrdersNetSales.getTitle(t),
      id: 'loyaltyOrdersNetSales',
    },
    !isOffer
      ? {
          label: apiDefinitions.membersWhoOrdered.getTitle(t),
          id: 'membersWhoOrdered',
        }
      : undefined,
    {
      label: isOffer
        ? apiDefinitions.mobileOrders.getTitle(t)
        : apiDefinitions.mobileLoyaltyOrders.getTitle(t),
      id: 'mobileLoyaltyOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.mobileOrdersPercent.getTitle(t)
        : apiDefinitions.mobileLoyaltyOrdersPercent.getTitle(t),
      id: 'mobileLoyaltyOrdersPercent',
    },
    {
      label: apiDefinitions.newMembersLocation.getTitle(t),
      id: 'newMembers',
    },
    {
      label: isOffer
        ? apiDefinitions.otherOrders.getTitle(t)
        : apiDefinitions.otherLoyaltyOrders.getTitle(t),
      id: 'otherLoyaltyOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.otherOrdersPercent.getTitle(t)
        : apiDefinitions.otherLoyaltyOrdersPercent.getTitle(t),
      id: 'otherLoyaltyOrdersPercent',
    },
    isOffer
      ? {
          label:
            apiDefinitions.redemptionRateDistributedAllTimeRedeemedInPeriod.getTitle(
              t,
            ),
          id: 'redemptionRateDistributedAllTimeRedeemedInPeriod',
        }
      : undefined,
    isOffer
      ? {
          label:
            apiDefinitions.redemptionRateDistributedAndRedeemedInPeriod.getTitle(
              t,
            ),
          id: 'redemptionRateDistributedAndRedeemedInPeriod',
        }
      : undefined,
    isOffer
      ? {
          label: apiDefinitions.redemptionsAllTimeDistributions.getTitle(t),
          id: 'redemptionsAllTimeDistributions',
        }
      : undefined,
    isOffer
      ? {
          label:
            apiDefinitions.redemptionsSelectedTimePeriodDistributions.getTitle(
              t,
            ),
          id: 'redemptionsSelectedTimePeriodDistributions',
        }
      : undefined,
    {
      label: isOffer
        ? apiDefinitions.scanToPayOrders.getTitle(t)
        : apiDefinitions.scanToPayLoyaltyOrders.getTitle(t),
      id: 'scanToPayLoyaltyOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.scanToPayOrdersPercent.getTitle(t)
        : apiDefinitions.scanToPayLoyaltyOrdersPercent.getTitle(t),
      id: 'scanToPayLoyaltyOrdersPercent',
    },
    isOffer
      ? {
          label: apiDefinitions.totalDeleted.getTitle(t),
          id: 'totalDeleted',
        }
      : undefined,
    {
      label: apiDefinitions.totalLoyaltyOrders.getTitle(t),
      id: 'totalLoyaltyOrders',
    },
    isOffer
      ? {
          label: apiDefinitions.totalOpened.getTitle(t),
          id: 'totalOpened',
        }
      : undefined,
    !isOffer
      ? {
          label: apiDefinitions.totalOrders.getTitle(t),
          id: 'totalOrders',
        }
      : undefined,
    isOffer
      ? {
          label: apiDefinitions.totalSaved.getTitle(t),
          id: 'totalSaved',
        }
      : undefined,
    {
      label: apiDefinitions.uniqueUserApp.getTitle(t),
      id: 'uniqueUserApp',
    },
    {
      label: apiDefinitions.uniqueUserInStore.getTitle(t),
      id: 'uniqueUserInStore',
    },
    {
      label: apiDefinitions.uniqueUserOther.getTitle(t),
      id: 'uniqueUserOther',
    },
    {
      label: apiDefinitions.uniqueUserScanToPay.getTitle(t),
      id: 'uniqueUserScanToPay',
    },
    {
      label: apiDefinitions.uniqueUserWeb.getTitle(t),
      id: 'uniqueUserWeb',
    },
    {
      label: isOffer
        ? apiDefinitions.webOrders.getTitle(t)
        : apiDefinitions.webLoyaltyOrders.getTitle(t),
      id: 'webLoyaltyOrders',
    },
    {
      label: isOffer
        ? apiDefinitions.webOrdersPercent.getTitle(t)
        : apiDefinitions.webLoyaltyOrdersPercent.getTitle(t),
      id: 'webLoyaltyOrdersPercent',
    },
  ].filter(Boolean) as Option[];

  return [
    stringSearchParam(isOffer ? 'offerdatatype' : 'datatype', {
      defaultValue: 'activeMembersEarnOrBurn',
      options: dataOptions,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('forms.fields.data.label'),
          }
        : undefined,
    }),
  ];
};

// export const useCampaignSearchParams = (isFilterVisible: boolean) => {
//   const { t } = useTranslation();
//   const [triggerListCampaigns] = useLazyListCampaignsQuery();

//   const allCampaignsOption = {
//     label: t('page.communications.offers.filters.campaigns.all'),
//     id: 'ALL',
//   };

//   return [
//     stringSearchParam('campaignId', {
//       defaultValue: 'ALL',
//       options: [allCampaignsOption],
//       asyncOptions: async () => {
//         const { data } = await triggerListCampaigns({
//           count: 1000,
//           page: 0,
//         });
//         return [allCampaignsOption].concat(
//           data?.campaigns?.map(campaign => ({
//             id: campaign.campaignId,
//             label: campaign.title,
//           })) ?? [],
//         );
//       },
//       asyncDependents: ['offerId'],
//       filter: isFilterVisible
//         ? {
//             type: 'SingleSelectFilter',
//             label: t('page.communications.offers.filters.campaigns.label'),
//           }
//         : undefined,
//     }),
//   ];
// };

export const useOfferSearchParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerAvailableOffers] = useLazyGetAvailableOffersQuery();

  const allOffersOption = {
    label: t('page.communications.offers.filters.campaigns.all'),
    id: 'ALL',
  };

  return [
    stringListSearchParam('offerIds', {
      defaultValue: ['ALL'],
      options: [allOffersOption],
      asyncOptions: async () => {
        const { data } = await triggerAvailableOffers(undefined, true);
        return [allOffersOption].concat(
          data?.offers?.map(offer => ({
            id: offer?.offerId,
            label: offer?.offerName,
          })) ?? [],
        );
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('forms.fields.offer.label'),
          }
        : undefined,
    }),
  ];
};

export const useOfferTypeFilter = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const offerTypeOptions = [
    {
      label: t('forms.fields.offerType.options.all'),
      id: 'ALL',
    },
    {
      label: t('forms.fields.offerType.options.loyaltyPointOffer'),
      id: 'loyaltyPointOffer',
    },
    {
      label: t('forms.fields.offerType.options.nonPointOffer'),
      id: 'nonPointOffer',
    },
  ];

  return [
    stringSearchParam('offertype', {
      defaultValue: 'ALL',
      options: offerTypeOptions,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('forms.fields.offerType.label'),
          }
        : undefined,
    }),
  ];
};

// export const useOfferStatusFilter = (isFilterVisible: boolean) => {
//   const { t } = useTranslation();
//   const statusOptions = [
//     {
//       label: t('page.communications.offers.filters.status.statusTypes.all'),
//       id: 'ALL',
//     },
//     {
//       label: t('page.communications.offers.filters.status.statusTypes.active'),
//       id: 'ACTIVE',
//     },
//     {
//       label: t(
//         'page.communications.offers.filters.status.statusTypes.scheduled',
//       ),
//       id: 'SCHEDULED',
//     },
//     {
//       label: t('page.communications.offers.filters.status.statusTypes.expired'),
//       id: 'EXPIRED',
//     },
//   ];

//   return [
//     stringSearchParam('offerstatus', {
//       defaultValue: 'ALL',
//       options: statusOptions,
//       filter: isFilterVisible
//         ? {
//             type: 'SingleSelectFilter',
//             label: t('page.communications.offers.filters.status.label'),
//           }
//         : undefined,
//     }),
//   ];
// };

const useAllSearchParams = (searchParamsConfig: SearchParamsConfig) => {
  return [
    ...useGranularitySearchParams(searchParamsConfig?.granularity),
    ...useDateParams(searchParamsConfig?.date),
    ...useComparisonDateParams(searchParamsConfig?.comparisondate),
    ...useDataTypeSearchParams(searchParamsConfig?.datatype, false),
    ...useDataTypeSearchParams(searchParamsConfig?.offerdatatype, true),
    ...useAdminStoreIdSearchParams(searchParamsConfig?.storeIds),
    // ...useCampaignSearchParams(searchParamsConfig?.campaignId),
    ...useOfferSearchParams(searchParamsConfig?.offerIds),
    // ...useOfferStatusFilter(searchParamsConfig?.offerStatus),
    ...useOfferTypeFilter(searchParamsConfig?.offertype),
    // Related to https://app.shortcut.com/incentivio/story/346328/allow-sorting-by-selected-column-in-location-and-offer-performance-reports
    // We are replacing ...defaultSearchParams so that we can prevent sort params from persisting across tabs. Persisting these search params
    // would have undesireable side effects. This solution isn't ideal either, but we consider it to be the lesser of two evils.
    ...[
      numberSearchParam('count', {
        defaultValue: 100,
      }),
      numberSearchParam('page', {
        defaultValue: 0,
      }),
      stringSearchParam('sortBy', {
        defaultValue: null,
        excludeFromUrl: true,
      }),
      stringSearchParam('sortDirection', {
        defaultValue: null,
        excludeFromUrl: true,
      }),
    ],
  ];
};

export const useBrandLoyaltyOverviewSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: true,
    storeIds: false,
    granularity: false,
    datatype: false,
    offerdatatype: false,
    offertype: false,
    // campaignId: false,
    offerIds: false,
    // offerStatus: false,
  });
};

export const useFranchiseLoyaltyOverviewSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: true,
    storeIds: true,
    granularity: false,
    datatype: false,
    offerdatatype: false,
    offertype: false,
    // campaignId: false,
    offerIds: false,
    // offerStatus: false,
  });
};

export const useLocationLoyaltyPerformanceSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: true,
    storeIds: true,
    granularity: false,
    datatype: false,
    offerdatatype: false,
    offertype: false,
    // campaignId: false,
    offerIds: false,
    // offerStatus: false,
  });
};

export const useLocationLoyaltyTrendsSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: false,
    storeIds: true,
    granularity: true,
    datatype: true,
    offerdatatype: false,
    offertype: false,
    // campaignId: false,
    offerIds: false,
    // offerStatus: false,
  });
};

export const useLoyaltyOfferPerformanceSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: true,
    storeIds: true,
    granularity: false,
    datatype: false,
    offerdatatype: false,
    offertype: true,
    // campaignId: true,
    offerIds: true,
    // offerStatus: true,
  });
};

export const useLoyaltyOfferTrendsSearchParams = () => {
  return useAllSearchParams({
    date: true,
    comparisondate: false,
    storeIds: true,
    granularity: true,
    datatype: false,
    offerdatatype: true,
    offertype: true,
    // campaignId: true,
    offerIds: true,
    // offerStatus: true,
  });
};
